import { UzBadge, UzDrawer, UzTabs } from '@utazzo/ui';
import { ChevronRight } from 'lucide-react';
import React from 'react';

import {
  SECTION_META,
  SECTION_TEMPLATES,
  SECTION_TYPES,
  type SectionType,
  type TemplateTone,
  sectionIcon,
  sectionLabel,
} from '../section-presets';

interface AddSectionDrawerProps {
  open: boolean;
  onClose: () => void;
  onAddSection: (type: SectionType) => void;
  onAddTemplate: (types: SectionType[]) => void;
}

const TONE_TO_BADGE: Record<TemplateTone, 'secondary' | 'outline' | 'success' | 'warning'> = {
  accent: 'secondary',
  info: 'outline',
  success: 'success',
  neutral: 'outline',
  warning: 'warning',
};

/**
 * "Add a section" drawer. Two tabs: a grid of single section types, and
 * multi-section presets that drop several sections in at once. Selecting
 * anything closes the drawer.
 */
export const AddSectionDrawer = ({
  open,
  onClose,
  onAddSection,
  onAddTemplate,
}: AddSectionDrawerProps): React.JSX.Element => {
  const renderTypeButton = (type: SectionType): React.JSX.Element => {
    const meta = SECTION_META[type];
    const Icon = meta.icon;
    return (
      <button
        key={type}
        type="button"
        onClick={() => onAddSection(type)}
        className="flex items-start gap-3 rounded-lg border border-(--border) bg-(--bg) p-3 text-left transition-colors hover:border-(--border-strong) hover:bg-(--z-50)"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-(--z-100) text-(--fg-muted)">
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium text-(--fg)">{meta.label}</span>
          <span className="text-[12px] leading-tight text-(--fg-faint)">{meta.hint}</span>
        </span>
      </button>
    );
  };

  // Ungrouped types stay in the default grid; grouped types (e.g. MICE) render
  // under their own small heading so related sections live together.
  const ungroupedTypes = SECTION_TYPES.filter((type) => !SECTION_META[type].group);
  const groupedTypes = SECTION_TYPES.reduce<Record<string, SectionType[]>>((acc, type) => {
    const group = SECTION_META[type].group;
    if (group) (acc[group] ??= []).push(type);
    return acc;
  }, {});

  const singleTab = (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {ungroupedTypes.map(renderTypeButton)}
      </div>
      {Object.entries(groupedTypes).map(([group, types]) => (
        <div key={group} className="flex flex-col gap-2">
          <h3 className="text-[11px] font-semibold tracking-wide text-(--fg-faint) uppercase">
            {group}
          </h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{types.map(renderTypeButton)}</div>
        </div>
      ))}
    </div>
  );

  const templateTab = (
    <div className="flex flex-col gap-2">
      {SECTION_TEMPLATES.map((tpl) => (
        <button
          key={tpl.id}
          type="button"
          onClick={() => onAddTemplate(tpl.sections)}
          className="flex flex-col gap-2.5 rounded-lg border border-(--border) bg-(--bg) p-3.5 text-left transition-colors hover:border-(--border-strong) hover:bg-(--z-50)"
        >
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-(--fg)">{tpl.name}</div>
              <div className="mt-0.5 text-[11.5px] text-(--fg-muted)">{tpl.description}</div>
            </div>
            <div className="flex shrink-0 gap-1">
              {tpl.badges.map((b) => (
                <UzBadge key={b} tone={TONE_TO_BADGE[tpl.tone]} className="text-[11px]">
                  {b}
                </UzBadge>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 border-t border-dashed border-(--border) pt-2.5">
            {tpl.sections.map((s, i) => {
              const Icon = sectionIcon(s);
              return (
                <React.Fragment key={`${s}-${i}`}>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-(--z-100) py-1 pr-2.5 pl-1 text-[12px] font-medium text-(--fg)">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-(--bg) text-(--fg-muted)">
                      <Icon className="h-2.5 w-2.5" />
                    </span>
                    {sectionLabel(s)}
                  </span>
                  {i < tpl.sections.length - 1 && (
                    <ChevronRight className="h-3 w-3 text-(--fg-faint)" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title="Add a section"
      description="Pick a single section type or drop in a multi-section preset."
    >
      <UzTabs
        tabs={[
          {
            value: 'single',
            label: 'Single section',
            count: SECTION_TYPES.length,
            content: singleTab,
          },
          {
            value: 'template',
            label: 'Presets',
            count: SECTION_TEMPLATES.length,
            content: templateTab,
          },
        ]}
      />
    </UzDrawer>
  );
};
